import { ExternalServiceData, StatusType, IntegrationType } from './../../../types/externalServiceData.type';
import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { AlloyStatus, AlloyVerificationResult } from '@workflow/ecp-workflow/dynamic-app/service/alloy/alloy.service';
import { dependencies } from '@workflow/class/workflow.dependency';
import { Observable, throwError } from 'rxjs';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { isPlatformBrowser } from '@angular/common';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';

@BuildingBlock('alloy-verification')
@Component({
  selector: 'ecp-alloy-verification',
  templateUrl: './alloy-verification.component.html',
  styleUrl: './alloy-verification.component.scss',
  standalone: false
})
export class AlloyVerificationComponent extends BuildingBlockControlBaseComponent implements OnInit {
  private readonly log = inject(SawDust);
  private readonly analyticsMetadataService = inject(AnalyticsMetadataService);
  private readonly googleTrackingService = inject(GoogleTrackingService);
  private readonly loader = inject(WorkflowLoaderService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private journey: AlloyVerificationResult;
  private mode: string;
  /**
   * Initializes the component and checks the current application journey,
   * or creates an application journey if none exists.
   */
  public ngOnInit(): void {
    // Start a card-level loader while we initialize / fetch journey data
    this.loader.start(undefined, { animationId: 'default', overlayMode: 'card' });
    // if alloy is disabled, skip verification and redirect to next page
    if (!this.applicationApiService.isAlloyEnabled()) {
      this.setApplicationStatus('ignored');
      this.navigatetoNextPage();
      this.loader.clear();
      return;
    }
    this.mode = this.field.id.startsWith('oao') ? 'api' : 'ola';
    // check if we already did this (back navigation or reload page)
    if (this.preventReverifyIfNeeded()) {
      return;
    }
    this.initialize().subscribe({
      next: (keys) => {
        const entityId = this.statefulService.wrap(this.mode + ':entityId').valueString;
        // works for create and update
        this.applicationApiService
          .startAlloyJourney(
            entityId,
            keys.neuroUserId,
            keys.neuroSiteId,
            keys.socureDeviceId,
            keys.threatMetrixSessionId,
            keys.iovationBlackboxId
          )
          .subscribe({
            next: (result) => {
              this.loadJourney(result);
            },
            error: (err) => {
              // [error] if black path, call next
              this.setApplicationStatus('error');

              this.navigatetoNextPage();
              this.loader.clear();
              throw err;
            }
          });
      },
      error: (err: Error) => {
        this.log.warn(err, { component: 'AlloyVerificationComponent' });
        this.loader.clear();
      }
    });
  }

  public setApplicationStatus(status: 'completed' | 'error' | 'ignored'): void {
    this.statefulService.wrap(`${this.mode}:journeyInfo/applicationStatus`).value = status;
    this.statefulService.wrap(`${this.mode}:journeyInfo/isAlloyCompleted`).value = true;
  }

  /**
   * Triggers a step up action - getting the appropriate context and showing alloy iframe.
   */
  public verifyIdentity(): void {
    const params = this.applicationApiService.getAlloyInitArgs(this.mode);

    const request: { journeyToken: string } = { journeyToken: params.journeyToken };
    this.applicationApiService
      .triggerAlloyStepup(params.sdkKey, params.journeyToken, this.journey.journeyApplicationId, params.sourcePublicToken)
      .subscribe({
        next: (result) => {
          this.saveAlloyJourneyInfo(result, this.mode);
          this.logExternalServiceData(JSON.stringify(request), JSON.stringify(result), null);
          this.saveJourneyOutcomeIntoArcOS(result);
          // route based on the response
          if (result.actionRequired === AlloyStatus.Rejected) {
            this.persistChangedValues();
            this.setApplicationStatus('completed');
            if (this.applicationApiService.isAlloyStopOnDeniedEnabled()) {
              this.navigateToOfframp();
            } else {
              this.navigatetoNextPage();
            }
            // GA Rejected from Step Up
            const data = this.analyticsMetadataService.getMetadata(this.workflowService.currentPage);
            const fixedName = data[0] + '-step-up-reject';
            this.googleTrackingService.publishEvent(fixedName, data[1]);
          } else {
            this.setApplicationStatus('completed');
            this.persistChangedValues();
            this.navigatetoNextPage();
            const data = this.analyticsMetadataService.getMetadata(this.workflowService.currentPage);
            const fixedName = data[0] + '-step-up-success';
            this.googleTrackingService.publishEvent(fixedName, data[1]);
            // GA Approved from step up probably but check action required. May require lender intervention.
          }
        },
        error: (err) => {
          this.log.withError(err).error('Alloy verification flow error', { component: 'AlloyVerificationComponent' });
          this.logExternalServiceData(JSON.stringify(request), null, JSON.stringify(err));
          if (err && err.event !== 'exit') {
            this.navigatetoNextPage();
            this.setApplicationStatus('error');
          }
          // they closed the popup, they still need to verify
        }
      });
  }

  private initialize(forceReInit?: boolean): Observable<{
    neuroUserId?: string;
    neuroSiteId?: string;
    socureDeviceId?: string;
    threatMetrixSessionId?: string;
    iovationBlackboxId?: string;
  }> {
    const params = this.applicationApiService.getAlloyInitArgs(this.mode);
    if (!params.journeyToken || !params.sdkKey) {
      // we are somehow in a broken state? try to save with redirect
      this.dynamicPageService.navigateNextPage();
      return throwError(() => new Error("invalid for verification we shouldn't have hit this"));
    }
    return this.applicationApiService.alloyTrackingAndInit(
      params.sdkKey,
      params.journeyToken,
      this.journey?.journeyApplicationId,
      params.sourcePublicToken,
      forceReInit
    );
  }

  /**
   * Stores the journey locally in the component,
   * stores the journey id on the bundle,
   * and determines what action to take, either step-up
   * or redirect.
   * @param result The result to store and use for determining action path.
   */
  private loadJourney(result: AlloyVerificationResult): void {
    this.journey = result;
    // save the app journey to the bundle
    this.saveAlloyJourneyInfo(result, this.mode);
    this.persistChangedValues();
    /**
     * [verified] if green path, call next
     * [rejected] if red path, call page of shame (WIP)
     * [step up] if yellow path, trigger loading off, show page, let user continue with step-up
     * [manual review] if brown path, call next page
     * [error] if black path, call next
     */

    if (result.actionRequired === AlloyStatus.Rejected) {
      // GA rejection event
      const data = this.analyticsMetadataService.getMetadata(this.workflowService.currentPage);
      const fixedName = data[0] + '-rejected';
      this.googleTrackingService.publishEvent(fixedName, data[1]);
      this.setApplicationStatus('completed');
      if (this.applicationApiService.isAlloyStopOnDeniedEnabled()) {
        this.navigateToOfframp();
      } else {
        this.navigatetoNextPage();
      }
    } else if (!result.stepUpRequired) {
      this.setApplicationStatus('completed');
      // GA Approved
      const data = this.analyticsMetadataService.getMetadata(this.workflowService.currentPage);
      const fixedName = data[0] + '-success';
      this.googleTrackingService.publishEvent(fixedName, data[1]);
      this.navigatetoNextPage();
    } else {
      // GA Step up state
      const data = this.analyticsMetadataService.getMetadata(this.workflowService.currentPage);
      this.googleTrackingService.publishEvent(data[0], data[1]);
    }

    // Save Alloy Journey outcome into arcOS
    this.saveJourneyOutcomeIntoArcOS(result);
    // Finished loading / decision made, clear loader so card content is interactive
    this.loader.clear();
  }

  /**
   * Captures important information from step up, or from journey creation.
   * @param alloyResponse The response object to save
   * @param key The current flow key, to store on the right bundle
   */
  private saveAlloyJourneyInfo(alloyResponse: AlloyVerificationResult, key: string): void {
    const appJourneyToken = this.statefulService.wrap(`${key}:applicationJourney`);
    appJourneyToken.value = alloyResponse.journeyApplicationId;
    const statusToken = this.statefulService.wrap(`${key}:journeyInfo/alloyStatus`);
    statusToken.value = alloyResponse.status;
    const outcomeToken = this.statefulService.wrap(`${key}:journeyInfo/alloyOutcome`);
    outcomeToken.value = alloyResponse.outcome;
  }

  /** Navigate to the next page on API failure, Empty PID report & irrespective of the mid-app decision flag */
  private navigatetoNextPage(): void {
    this.dynamicPageService.navigateNextPage();
  }

  /** Navigate to the offramp page. */
  private navigateToOfframp(): void {
    this.router.navigateByUrl('/contact-us');
  }

  private persistChangedValues(): void {
    const appJourneyToken = this.statefulService.wrap(`${this.mode}:applicationJourney`);
    const statusToken = this.statefulService.wrap(`${this.mode}:journeyInfo/alloyStatus`);
    const outcomeToken = this.statefulService.wrap(`${this.mode}:journeyInfo/alloyOutcome`);
    appJourneyToken.save();
    statusToken.save();
    outcomeToken.save();
    this.applicationApiService.ensureAlloySaved(this.mode === 'api' ? 'oao' : 'ola');
  }

  private preventReverifyIfNeeded(): boolean {
    const appJourneyToken =
      this.statefulService.wrap(`${this.mode}:applicationJourney`).valueString ??
      dependencies.stateManagementService.getValue<string>(`${this.mode}:applicationJourney`);
    const statusToken =
      this.statefulService.wrap(`${this.mode}:journeyInfo/alloyStatus`).valueString?.toLowerCase() ??
      dependencies.stateManagementService.getValue<string>(`${this.mode}:journeyInfo/alloyStatus`);
    const outcomeToken =
      this.statefulService.wrap(`${this.mode}:journeyInfo/alloyOutcome`).valueString?.toLowerCase() ??
      dependencies.stateManagementService.getValue<string>(`${this.mode}:journeyInfo/alloyOutcome`);
    if (appJourneyToken) {
      const result = {
        journeyApplicationId: appJourneyToken,
        status: statusToken,
        outcome: outcomeToken
      } as AlloyVerificationResult;
      if (statusToken === 'completed' && outcomeToken === 'denied') {
        // Save Alloy Journey outcome into arcOS
        this.saveJourneyOutcomeIntoArcOS(result);
        if (this.applicationApiService.isAlloyStopOnDeniedEnabled()) {
          this.navigateToOfframp();
        } else {
          this.navigatetoNextPage();
        }
        return true;
      } else if (
        (statusToken === 'completed' && outcomeToken === 'approved') ||
        ['error', 'pending_journey_application_review', 'pending_reconciliation', 'waiting_review'].includes(statusToken)
      ) {
        // Save Alloy Journey outcome into arcOS
        this.saveJourneyOutcomeIntoArcOS(result);
        this.navigatetoNextPage();
        return true;
      }
    }
    return false;
  }

  /**
   * Log response data into arcOS
   * @param requestData RequestData String
   * @param responseData Response Object json string
   * @param err Error object json string
   */
  private logExternalServiceData(requestData: string, responseData: string, error: string): void {
    const externalData = {} as ExternalServiceData;

    // Get the Application number
    const flowType = this.isBrowser ? sessionStorage.getItem('BMP.FlowType') ?? 'OLA' : 'OLA';
    let appNumber = null;
    if (flowType.includes('OLA')) {
      appNumber = this.statefulService.wrap('ola:borrowerInfo/applicationID').valueString;
    } else {
      appNumber = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
    }

    externalData.applicationId = appNumber;
    externalData.requestData = requestData;
    externalData.lenderCode = this.lenderService.lenderData.consumerLoanCode;
    externalData.integrationTypeId = IntegrationType.DX_Alloy_StepupVerification;

    if (error) {
      externalData.exception = error;
      externalData.responseData = error;
      externalData.statusTypeId = StatusType.Fail;
    } else {
      externalData.responseData = responseData;
      externalData.statusTypeId = StatusType.Success;
    }

    this.applicationApiService.logExternalServiceDataToArcOS(externalData).subscribe();
  }

  /**
   * Save Alloy outcome into ArcOS
   */
  private saveJourneyOutcomeIntoArcOS(alloyResponse: AlloyVerificationResult): void {
    let appId: string = null;

    if (this.mode === 'api') {
      appId = this.statefulService.wrap(`${this.mode}:oaoMemberInformation/accountsAndServices/applicationId`).valueString;
    } else if (this.mode === 'ola') {
      appId = this.statefulService.wrap(`${this.mode}:borrowerInfo/applicationID`).valueString;
    }

    if (appId) {
      const journey = {
        applicationId: appId,
        applicantName:
          this.statefulService.wrap(`${this.mode}:firstName`).valueString +
          ' ' +
          this.statefulService.wrap(`${this.mode}:lastName`).valueString,
        journeyApplicationId: alloyResponse.journeyApplicationId,
        status: alloyResponse.status,
        outcome: alloyResponse.outcome
      } as AlloyVerificationResult;

      // Before redirect to Contact us page, lets save Alloy outcome into arcOS
      this.applicationApiService.saveAlloyJourneyOutcomeToArcOS(journey).subscribe({
        next: () => {},
        error: () => {}
      });
    }
  }
}
